import { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isIOS, isFlutterApp } from '@ecs-uk/web-common-mobile-view-helpers';

import { PaymentMethodTypes } from '../../PaymentCaptureMethod/constants.ts';
import { splitsList } from '../../../constants';
import useExistingPaymentMethod from '../../PaymentCaptureMethod/hooks/useExistingPaymentMethod';
import useSplits from '../../../hooks/useSplits';
import { PaymentContext } from '../PaymentContextProvider';
import {
  trackApplePayFeatureAvailability,
  trackApplePayWebRollout,
  trackGooglePayFeatureAvailability,
} from '../../../helpers/switch/analytics';

const usePaymentMethods = () => {
  const { isWebview, isMethodChange } = useContext(PaymentContext);
  const { existingPaymentType } = useExistingPaymentMethod();
  const splitReady = useSelector(state => state.split?.fetched);

  const splits = useSplits([
    splitsList.applePayWebRollout,
    splitsList.disableApplePayRecurly,
    splitsList.googlePayWebRollout,
    splitsList.disableGooglePayRecurly,
    splitsList.disableGooglePayOnAndroid,
  ]);
  const disableApplePay = splits[splitsList.disableApplePayRecurly];
  const applePayUpsellTest = splits[splitsList.applePayWebRollout];

  const disableGooglePay = splits[splitsList.disableGooglePayRecurly];
  const disableGooglePayOnAndroid = splits[splitsList.disableGooglePayOnAndroid];
  const googlePayUpsellTest = splits[splitsList.googlePayWebRollout];

  const alreadyUsingApplePay = existingPaymentType === PaymentMethodTypes.ApplePay;
  const splitTreatment = applePayUpsellTest ? 'on' : 'off';

  const applePaySupported = typeof window !== 'undefined' && !!window?.ApplePaySession?.canMakePayments?.();
  const isEligibleForApplePayWebTest = !isMethodChange && !isWebview;

  const isIOSFlutterApp = isIOS() && isFlutterApp();
  const isAndroidFlutterApp = !isIOS() && isFlutterApp();

  const applePayAllowed = splitReady && applePaySupported && !disableApplePay;
  const googlePayAllowed =
    splitReady &&
    !isIOSFlutterApp &&
    !(isAndroidFlutterApp && disableGooglePayOnAndroid) &&
    googlePayUpsellTest &&
    !disableGooglePay;

  const availableMethods = useMemo(() => {
    const paymentMethods = [PaymentMethodTypes.Card];
    const changeMethods = [PaymentMethodTypes.Card];

    if (googlePayAllowed) {
      paymentMethods.push(PaymentMethodTypes.GooglePay);
      changeMethods.push(PaymentMethodTypes.GooglePay);
    }
    if (applePayAllowed) {
      if (isMethodChange) {
        changeMethods.push(PaymentMethodTypes.ApplePay);
      }
      if (isWebview || alreadyUsingApplePay || applePayUpsellTest) {
        paymentMethods.push(PaymentMethodTypes.ApplePay);
      }
    }

    return isMethodChange ? changeMethods : paymentMethods;
  }, [
    splitReady,
    applePaySupported,
    disableApplePay,
    disableGooglePay,
    disableGooglePayOnAndroid,
    isMethodChange,
    isWebview,
    alreadyUsingApplePay,
    applePayUpsellTest,
    googlePayUpsellTest,
    isIOSFlutterApp,
    isAndroidFlutterApp,
  ]);

  useEffect(() => {
    if (!splitReady) return;

    if (applePaySupported) {
      if (disableApplePay) {
        trackApplePayFeatureAvailability('off');
      } else {
        trackApplePayFeatureAvailability('on');
        if (isEligibleForApplePayWebTest) {
          trackApplePayWebRollout(alreadyUsingApplePay ? 'existing' : splitTreatment);
        }
      }
    }

    if (googlePayAllowed) {
      trackGooglePayFeatureAvailability('on');
    } else {
      trackGooglePayFeatureAvailability('off');
    }
  }, [
    splitReady,
    applePaySupported,
    disableApplePay,
    disableGooglePay,
    disableGooglePayOnAndroid,
    googlePayUpsellTest,
    googlePayAllowed,
    isEligibleForApplePayWebTest,
    alreadyUsingApplePay,
    splitTreatment,
  ]);

  return availableMethods;
};

export default usePaymentMethods;
